/**
 * AnswerOption - Individual Answer Option Component
 * 
 * Displays a single answer option with various states and effects.
 * 
 * Features:
 * - Selection state (normal, selected, correct, incorrect)
 * - Joker effects (hidden, audience %, best player indicator)
 * - Multiple choice support
 * - Disabled state (submitted/locked)
 * - Animations and transitions
 */

import React from 'react';
import { useTheme } from '../../../context/ThemeContext';
import { SmartTextRenderer } from '../../ui/MathRenderer';

/**
 * Letter labels for options (A, B, C, D, ...)
 */
const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

/**
 * AnswerOption component
 * @param {number} index - Option index
 * @param {string} text - Option text content
 * @param {boolean} isSelected - Whether option is selected
 * @param {boolean} isCorrect - Whether option is correct (after reveal)
 * @param {boolean} showResult - Whether to show correct/incorrect state
 * @param {boolean} isHidden - Whether option is hidden (50:50 joker)
 * @param {number} audiencePercent - Audience vote percentage (audience joker)
 * @param {number} audienceCount - Absolute number of votes for this option
 * @param {boolean} isBestPlayerChoice - Whether top player chose this (best player joker)
 * @param {boolean} disabled - Whether option is disabled (submitted/locked)
 * @param {boolean} allowMultiple - Whether multiple selection is allowed
 * @param {Function} onClick - Click handler
 */
export function AnswerOption({
  index,
  text,
  isSelected = false,
  isCorrect = false,
  showResult = false,
  isHidden = false,
  audiencePercent = null,
  audienceCount = undefined,
  isBestPlayerChoice = false,
  disabled = false,
  allowMultiple = false,
  onClick,
}) {
  const { darkMode } = useTheme();
  
  const letter = OPTION_LETTERS[index] || (index + 1).toString();

  // Hidden by 50:50 joker
  if (isHidden) {
    return (
      <div
        className={`
          p-4 rounded-xl border-2 border-dashed
          ${darkMode ? 'border-gray-700 bg-gray-800/30' : 'border-gray-300 bg-gray-100/50'}
          opacity-40
          flex items-center gap-4
        `}
      >
        <span className={`
          w-10 h-10 rounded-full flex items-center justify-center
          ${darkMode ? 'bg-gray-700 text-gray-500' : 'bg-gray-200 text-gray-400'}
          font-bold text-lg
        `}>
          {letter}
        </span>
        <span className={`text-lg ${darkMode ? 'text-gray-600' : 'text-gray-400'} line-through`}>
          Ausgeblendet
        </span>
      </div>
    );
  }

  // Determine background color based on state
  const getBgClass = () => {
    if (showResult && isSelected) {
      return isCorrect
        ? darkMode ? 'bg-green-900/40' : 'bg-green-50'
        : darkMode ? 'bg-red-900/40' : 'bg-red-50';
    }
    if (showResult && isCorrect) {
      return darkMode ? 'bg-green-900/40' : 'bg-green-50';
    }
    if (isSelected) {
      return darkMode ? 'bg-indigo-900/40' : 'bg-indigo-50';
    }
    return darkMode ? 'bg-gray-800' : 'bg-white';
  };

  // Determine border color based on state
  const getBorderClass = () => {
    if (showResult && isSelected) {
      return isCorrect
        ? 'border-green-500'
        : 'border-red-500';
    }
    if (showResult && isCorrect) {
      return 'border-green-500';
    }
    if (isSelected) {
      return 'border-indigo-500';
    }
    return darkMode ? 'border-gray-700' : 'border-gray-200';
  };

  // Determine text color
  const getTextClass = () => {
    if (showResult && isSelected && !isCorrect) {
      return darkMode ? 'text-red-400' : 'text-red-700';
    }
    if (showResult && isCorrect) {
      return darkMode ? 'text-green-400' : 'text-green-700';
    }
    if (isSelected) {
      return darkMode ? 'text-indigo-300' : 'text-indigo-700';
    }
    return darkMode ? 'text-gray-200' : 'text-gray-800';
  };

  // Letter badge color
  const getLetterBgClass = () => {
    if (showResult && isSelected && !isCorrect) {
      return 'bg-red-500 text-white';
    }
    if (showResult && isCorrect) {
      return 'bg-green-500 text-white';
    }
    if (isSelected) {
      return 'bg-indigo-500 text-white';
    }
    return darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600';
  };

  return (
    <button
      onClick={() => !disabled && onClick?.(index)}
      disabled={disabled}
      className={`
        w-full p-4 rounded-xl border-2 
        ${getBgClass()}
        ${getBorderClass()}
        ${disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-[1.01]'}
        transition-all duration-200
        text-left
        flex items-center gap-4
        relative
        ${isSelected ? 'ring-2 ring-offset-2 ring-indigo-500/50' : ''}
        ${showResult && isCorrect ? 'ring-2 ring-offset-2 ring-green-500/50 animate-pulse' : ''}
      `}
    >
      {/* Letter badge */}
      <span
        className={`
          w-10 h-10 rounded-full flex items-center justify-center
          ${getLetterBgClass()}
          font-bold text-lg flex-shrink-0
          theme-transition duration-200
        `}
      >
        {letter}
      </span>

      {/* Option text */}
      <span className={`text-lg ${getTextClass()} flex-grow`}>
        <SmartTextRenderer text={text} />
      </span>

      {/* Audience joker percentage and count */}
      {audiencePercent !== null && (
        <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
          <div className={`
            px-3 py-1 rounded-full text-sm font-medium
            ${darkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'}
          `}>
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>{audiencePercent}%</span>
              {audienceCount !== undefined && (
                <span className={darkMode ? 'text-blue-400' : 'text-blue-600'}>
                  ({audienceCount})
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Best player joker indicator */}
      {isBestPlayerChoice && (
        <div className="absolute -top-2 -right-2">
          <div className={`
            px-2 py-1 rounded-full text-xs font-medium
            ${darkMode ? 'bg-yellow-900/80 text-yellow-300' : 'bg-yellow-100 text-yellow-700'}
            flex items-center gap-1
            shadow-lg
          `}>
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            Führend
          </div>
        </div>
      )}

      {/* Correct/Incorrect icons */}
      {showResult && (
        <div className="absolute top-2 right-2">
          {isSelected && isCorrect && (
            <svg className="w-6 h-6 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          )}
          {isSelected && !isCorrect && (
            <svg className="w-6 h-6 text-red-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          )}
          {!isSelected && isCorrect && (
            <svg className="w-6 h-6 text-green-500 opacity-75" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          )}
        </div>
      )}
    </button>
  );
}

/**
 * AnswerOptionList - Container for multiple options
 */
export function AnswerOptionList({
  options = [],
  selectedOptions = [],
  correctIndices = [],
  showResult = false,
  hiddenOptions = [],
  audienceResults = null,
  bestPlayerChoices = [],
  allowMultiple = false,
  disabled = false,
  onSelect,
}) {
  return (
    <div className="flex flex-col gap-3">
      {options.map((option, index) => (
        <AnswerOption
          key={index}
          index={index}
          text={typeof option === 'string' ? option : option.text || option.label || `Option ${index + 1}`}
          isSelected={selectedOptions.includes(index)}
          isCorrect={correctIndices.includes(index)}
          showResult={showResult}
          isHidden={hiddenOptions.includes(index)}
          audiencePercent={audienceResults?.percentages?.[index] ?? audienceResults?.[index] ?? null}
          audienceCount={audienceResults?.counts?.[index]}
          isBestPlayerChoice={bestPlayerChoices.includes(index)}
          allowMultiple={allowMultiple}
          disabled={disabled}
          onClick={onSelect}
        />
      ))}
    </div>
  );
}

export default AnswerOption;
